import { useMemo, useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useApp } from "../../context/AppContext";
import {
  HELP_SECTIONS,
  SYSTEM_OVERVIEW,
  GETTING_STARTED,
  ROLES,
  WORKFLOW_PHASES,
  MODULES,
  USE_CASES,
  BEST_PRACTICES,
  GLOSSARY,
  FAQS,
  TROUBLESHOOTING,
  LIFECYCLE_STAGES,
  buildHelpGuidePdfSections,
} from "../../data/helpGuideContent";
import LiveTourPanel from "../../components/help/LiveTourPanel";
import InteractiveProductTour from "../../components/help/InteractiveProductTour";
// NOTE: adjust this path if your project keeps this helper somewhere
// other than src/utils/ (it's the same one FormSubmissionsPage uses).
import { exportSubmissionPdf } from "../../utils/exportSubmissionPdf";
import {
  Search,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  BookOpen,
  Sparkles,
  MapPin,
  ArrowRight,
  Lightbulb,
  HelpCircle,
  Wrench,
  Route,
  Layers,
  ExternalLink,
  CheckCircle2,
  AlertCircle,
  X,
  MousePointer2,
  Download,
  Loader2,
} from "lucide-react";

// Maps each HELP_SECTIONS id to the icon/color used in its stepper dot
// and content-panel header. Order here drives the wizard's step order.
const DOC_STEP_META = {
  overview: { icon: Sparkles, color: "#6366f1" },
  "getting-started": { icon: MousePointer2, color: "#0ea5e9" },
  roles: { icon: Layers, color: "#6366f1" },
  journey: { icon: Route, color: "#0ea5e9" },
  modules: { icon: BookOpen, color: "#6366f1" },
  "use-cases": { icon: Lightbulb, color: "#f59e0b" },
  "best-practices": { icon: CheckCircle2, color: "#059669" },
  glossary: { icon: BookOpen, color: "#8b5cf6" },
  faq: { icon: HelpCircle, color: "#6366f1" },
  troubleshooting: { icon: Wrench, color: "#dc2626" },
};

// Sections where the hero search box is actually useful — the wizard
// jumps here automatically when a search starts returning matches.
const SEARCHABLE_DOC_STEPS = [
  "modules",
  "glossary",
  "faq",
  "troubleshooting",
];

const GLASS = {
  background: "rgba(255,255,255,0.78)",
  backdropFilter: "blur(20px)",
  WebkitBackdropFilter: "blur(20px)",
  border: "1px solid rgba(255,255,255,0.92)",
  boxShadow:
    "0 4px 24px rgba(37,99,235,0.07), 0 1px 3px rgba(15,23,42,0.04), inset 0 1px 0 rgba(255,255,255,1)",
};

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.06, duration: 0.45, ease: [0.22, 1, 0.36, 1] },
  }),
};

function GlassCard({ children, className = "", delay = 0, id }) {
  return (
    <motion.section
      id={id}
      custom={delay}
      variants={fadeUp}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-40px" }}
      className={`rounded-2xl overflow-hidden ${className}`}
      style={GLASS}
    >
      {children}
    </motion.section>
  );
}

function SectionHeader({ icon: Icon, title, subtitle, color = "#6366f1" }) {
  return (
    <div className="flex items-start gap-3 mb-5">
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
        style={{ background: `${color}18`, color }}
      >
        <Icon size={20} />
      </div>
      <div>
        <h2 className="text-lg font-display font-bold text-slate-800">
          {title}
        </h2>
        {subtitle && (
          <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>
        )}
      </div>
    </div>
  );
}

function ModuleCard({ module, defaultOpen = false, onNavigate }) {
  const [open, setOpen] = useState(defaultOpen);
  const Icon = module.icon;
  const canNavigate = module.path && onNavigate;

  return (
    <div
      className="rounded-xl border border-slate-200/80 bg-white/60 overflow-hidden transition-shadow hover:shadow-md"
      style={{ borderLeftWidth: 3, borderLeftColor: module.color }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-slate-50/80 transition-colors"
      >
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
          style={{ background: `${module.color}15`, color: module.color }}
        >
          <Icon size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-slate-800 text-sm">{module.title}</p>
          <p className="text-xs text-slate-500 truncate">{module.location}</p>
        </div>
        <ChevronDown
          size={16}
          className={`text-slate-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 pt-0 space-y-4 border-t border-slate-100">
              <p className="text-sm text-slate-600 leading-relaxed pt-3">
                {module.purpose}
              </p>

              <div className="grid sm:grid-cols-2 gap-3">
                <DetailBlock title="What you can do" items={module.features} />
                <DetailBlock title="Step by step" items={module.steps} numbered />
              </div>

              <div className="grid sm:grid-cols-3 gap-3 text-xs">
                <TagBlock
                  label="You will need"
                  items={module.inputs}
                  color="#6366f1"
                />
                <TagBlock
                  label="You will get"
                  items={module.outputs}
                  color="#059669"
                />
                <TagBlock
                  label="Works with"
                  items={module.connections}
                  color="#0ea5e9"
                />
              </div>

              {canNavigate && (
                <button
                  type="button"
                  onClick={() => onNavigate(module.path)}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-700 transition-colors"
                >
                  Open {module.title}
                  <ExternalLink size={14} />
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function DetailBlock({ title, items, numbered = false }) {
  return (
    <div className="rounded-lg bg-slate-50/80 p-3">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
        {title}
      </p>
      <ul className="space-y-1.5">
        {items.map((item, i) => (
          <li
            key={i}
            className="text-xs text-slate-600 flex gap-2 leading-relaxed"
          >
            {numbered ? (
              <span className="text-indigo-500 font-semibold shrink-0">
                {i + 1}.
              </span>
            ) : (
              <CheckCircle2
                size={12}
                className="text-emerald-500 shrink-0 mt-0.5"
              />
            )}
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

function TagBlock({ label, items, color }) {
  return (
    <div className="rounded-lg bg-slate-50/80 p-3">
      <p className="text-xs font-semibold mb-2" style={{ color }}>
        {label}
      </p>
      <div className="flex flex-wrap gap-1">
        {items.map((item) => (
          <span
            key={item}
            className="text-[10px] px-2 py-0.5 rounded-full bg-white border border-slate-200 text-slate-600"
          >
            {item}
          </span>
        ))}
      </div>
    </div>
  );
}

function FaqItem({ item, index }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200/80 rounded-xl overflow-hidden bg-white/50">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-slate-50/80 transition-colors"
      >
        <HelpCircle size={16} className="text-indigo-500 shrink-0" />
        <span className="flex-1 text-sm font-medium text-slate-800">
          {item.q}
        </span>
        <ChevronDown
          size={14}
          className={`text-slate-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <p className="px-4 pb-4 pt-0 text-sm text-slate-600 leading-relaxed pl-11">
              {item.a}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function GlossaryItem({ item }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-violet-100/80 rounded-xl overflow-hidden bg-white/50">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-violet-50/40 transition-colors"
      >
        <BookOpen size={16} className="text-violet-500 shrink-0" />
        <span className="flex-1 text-sm font-semibold text-slate-800">
          {item.term}
        </span>
        <ChevronDown
          size={14}
          className={`text-slate-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <p className="px-4 pb-4 pt-0 text-sm text-slate-600 leading-relaxed pl-11">
              {item.definition}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function GettingStartedChecklist({ checklist, role }) {
  const [checked, setChecked] = useState({});
  const isMine = checklist.role === role;

  const toggle = (index) => {
    setChecked((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const doneCount = checklist.steps.filter((_, i) => checked[i]).length;

  return (
    <div
      className={`rounded-xl border p-4 transition-all ${
        isMine
          ? "border-sky-200 bg-sky-50/50 ring-2 ring-sky-100"
          : "border-slate-200/80 bg-white/40"
      }`}
    >
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <span
            className="text-[10px] font-bold px-2 py-0.5 rounded-full text-white"
            style={{ background: checklist.color }}
          >
            {checklist.label.split(" — ")[0]}
          </span>
          {isMine && (
            <span className="text-[10px] text-sky-700 font-semibold">
              Recommended for you
            </span>
          )}
        </div>
        <span className="text-[10px] text-slate-500 font-medium">
          {doneCount}/{checklist.steps.length} done
        </span>
      </div>
      <p className="text-xs text-slate-500 mb-3">
        {checklist.label.includes(" — ")
          ? checklist.label.split(" — ")[1]
          : checklist.label}
      </p>
      <ul className="space-y-2">
        {checklist.steps.map((step, i) => (
          <li key={i}>
            <button
              type="button"
              onClick={() => toggle(i)}
              className="w-full flex items-start gap-2.5 text-left group"
            >
              <span
                className={`mt-0.5 w-5 h-5 rounded-md border flex items-center justify-center shrink-0 transition-colors ${
                  checked[i]
                    ? "bg-emerald-500 border-emerald-500 text-white"
                    : "border-slate-300 bg-white group-hover:border-indigo-300"
                }`}
              >
                {checked[i] && <CheckCircle2 size={12} />}
              </span>
              <span
                className={`text-xs leading-relaxed ${
                  checked[i]
                    ? "text-slate-400 line-through"
                    : "text-slate-700"
                }`}
              >
                {step}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function HelpGuidePage() {
  const { currentUser } = useApp();
  const navigate = useNavigate();
  const role = currentUser?.role || "user";
  const [viewMode, setViewMode] = useState("tour");
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState(role);
  const [docStep, setDocStep] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  const contentPanelRef = useRef(null);

  const activeSectionId = HELP_SECTIONS[docStep]?.id || "overview";

  useEffect(() => {
    setRoleFilter(role);
  }, [role]);

  const roleInfo = ROLES.find((r) => r.id === role) || ROLES[2];

  const filteredModules = useMemo(() => {
    const q = search.trim().toLowerCase();
    return MODULES.filter((m) => {
      const roleMatch =
        roleFilter === "all" ||
        m.roles.includes(roleFilter) ||
        m.roles.includes(role);
      if (!roleMatch) return false;
      if (!q) return true;
      const blob = [
        m.title,
        m.purpose,
        m.location,
        ...m.features,
        ...m.steps,
        ...m.connections,
      ]
        .join(" ")
        .toLowerCase();
      return blob.includes(q);
    });
  }, [search, roleFilter, role]);

  const filteredFaqs = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return FAQS;
    return FAQS.filter(
      (f) => f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q),
    );
  }, [search]);

  const filteredTrouble = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return TROUBLESHOOTING;
    return TROUBLESHOOTING.filter(
      (t) =>
        t.issue.toLowerCase().includes(q) ||
        t.causes.join(" ").toLowerCase().includes(q) ||
        t.fix.toLowerCase().includes(q),
    );
  }, [search]);

  const filteredGlossary = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return GLOSSARY;
    return GLOSSARY.filter(
      (g) =>
        g.term.toLowerCase().includes(q) ||
        g.definition.toLowerCase().includes(q),
    );
  }, [search]);

  const goToDocStep = (id) => {
    const idx = HELP_SECTIONS.findIndex((s) => s.id === id);
    if (idx === -1) return;
    setDocStep(idx);
    contentPanelRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  const nextDocStep = () => {
    setDocStep((i) => Math.min(i + 1, HELP_SECTIONS.length - 1));
    contentPanelRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  const prevDocStep = () => {
    setDocStep((i) => Math.max(i - 1, 0));
    contentPanelRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  // While searching, jump to a section that actually has matches instead
  // of leaving the person staring at an unrelated, unfiltered step.
  useEffect(() => {
    if (!search.trim()) return;
    if (SEARCHABLE_DOC_STEPS.includes(activeSectionId)) return;
    if (filteredModules.length > 0) goToDocStep("modules");
    else if (filteredGlossary.length < GLOSSARY.length) goToDocStep("glossary");
    else if (filteredFaqs.length < FAQS.length) goToDocStep("faq");
    else if (filteredTrouble.length < TROUBLESHOOTING.length)
      goToDocStep("troubleshooting");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleModuleNavigate = (path) => {
    if (!path) return;
    if (
      role === "user" ||
      path.startsWith(`/${role === "tenant_admin" ? "tenant" : role}`)
    ) {
      navigate(path);
      return;
    }
    navigate(path);
  };

  // Same helper FormSubmissionsPage/module exports use — wraps apiFetch
  // (correct base URL + auth) and POST /reports/export-pdf, and handles
  // the blob download + real filename from Content-Disposition itself.
  const handleDownloadGuidePdf = async () => {
    setDownloading(true);
    setDownloadError(null);
    try {
      await exportSubmissionPdf({
        title: "OneDPDP Help Guide",
        subtitle: `Reference documentation — ${roleInfo.label} view`,
        module: "Help Guide",
        organization:
          currentUser?.organization?.name || currentUser?.tenantName || "",
        filename: "OneDPDP_Help_Guide.pdf",
        sections: buildHelpGuidePdfSections(role),
      });
    } catch (err) {
      setDownloadError(
        err?.message || "Couldn't generate the PDF. Please try again.",
      );
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      className="min-h-full -m-4 lg:-m-6 font-sans relative overflow-hidden"
      style={{
        background:
          "linear-gradient(160deg, #f8fafc 0%, #eff6ff 30%, #eef2ff 55%, #f0f9ff 100%)",
      }}
    >
      {/* Ambient blobs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <motion.div
          className="absolute -top-24 -right-24 w-[480px] h-[480px] rounded-full"
          style={{
            background:
              "radial-gradient(circle, rgba(99,102,241,0.1) 0%, transparent 65%)",
          }}
          animate={{ x: [0, -20, 0], y: [0, 30, 0] }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute bottom-0 -left-32 w-[400px] h-[400px] rounded-full"
          style={{
            background:
              "radial-gradient(circle, rgba(14,165,233,0.08) 0%, transparent 65%)",
          }}
          animate={{ x: [0, 25, 0], y: [0, -20, 0] }}
          transition={{
            duration: 14,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1,
          }}
        />
      </div>

      <div className="relative z-10 w-full px-4 sm:px-5 lg:px-6 xl:px-8 py-6 lg:py-8">
        {/* Hero */}
        <motion.header
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative rounded-3xl overflow-hidden mb-6"
          style={{
            background:
              "linear-gradient(135deg, #0a1628 0%, #0f2d6b 50%, #112366 100%)",
            boxShadow: "0 8px 32px rgba(15,23,42,0.2)",
          }}
        >
          <div
            className="absolute inset-0 opacity-60 pointer-events-none"
            style={{
              background:
                "radial-gradient(ellipse 70% 80% at 10% 20%, rgba(99,102,241,0.5) 0%, transparent 50%), radial-gradient(ellipse 60% 60% at 90% 80%, rgba(14,165,233,0.35) 0%, transparent 50%)",
            }}
          />
          <div className="relative p-6 sm:p-8 lg:p-10">
            <div className="flex flex-col xl:grid xl:grid-cols-[minmax(0,1fr)_minmax(280px,360px)] xl:items-start gap-6">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-sky-200 text-xs font-medium mb-4">
                  <BookOpen size={14} />
                  Help & Documentation
                </div>
                <h1 className="text-2xl sm:text-3xl font-display font-bold text-white mb-2">
                  Welcome to {SYSTEM_OVERVIEW.title}
                </h1>
                <p className="text-sky-100/90 text-sm sm:text-base lg:text-lg leading-relaxed">
                  {SYSTEM_OVERVIEW.tagline}. Use the Interactive Tour for a
                  guided walkthrough, or read the step-by-step documentation
                  below — written in plain language for everyone on your team.
                </p>
                <div className="flex flex-wrap gap-2 mt-4">
                  <span
                    className="text-xs px-3 py-1 rounded-full font-medium"
                    style={{
                      background: `${roleInfo.color}30`,
                      color: "#e0e7ff",
                    }}
                  >
                    Viewing as {roleInfo.label}
                  </span>
                  <span className="text-xs px-3 py-1 rounded-full bg-white/10 text-sky-200 border border-white/15">
                    DPDP deadline: {SYSTEM_OVERVIEW.deadline}
                  </span>
                </div>

                {/* View mode tabs */}
                <div className="flex flex-wrap items-center gap-3 mt-5">
                  <div className="flex flex-wrap gap-2 p-1 rounded-xl bg-white/10 border border-white/15 w-fit">
                    <button
                      type="button"
                      onClick={() => setViewMode("tour")}
                      className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-all ${
                        viewMode === "tour"
                          ? "bg-white text-indigo-700 shadow-md"
                          : "text-sky-100 hover:bg-white/10"
                      }`}
                    >
                      <MousePointer2 size={14} />
                      Interactive Tour
                    </button>
                    <button
                      type="button"
                      onClick={() => setViewMode("docs")}
                      className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-all ${
                        viewMode === "docs"
                          ? "bg-white text-indigo-700 shadow-md"
                          : "text-sky-100 hover:bg-white/10"
                      }`}
                    >
                      <BookOpen size={14} />
                      Documentation
                    </button>
                  </div>

                  <button
                    type="button"
                    onClick={handleDownloadGuidePdf}
                    disabled={downloading}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/10 border border-white/20 text-sky-100 text-xs font-semibold hover:bg-white/20 transition-colors disabled:opacity-60"
                    title="Download the full guide as a PDF"
                  >
                    {downloading ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Download size={14} />
                    )}
                    {downloading ? "Preparing PDF…" : ""}
                  </button>
                </div>
                {downloadError && (
                  <p className="text-xs text-red-300 mt-2">{downloadError}</p>
                )}
              </div>

              {/* Search — docs mode only */}
              {viewMode === "docs" && (
                <div className="w-full xl:w-auto shrink-0">
                  <div className="relative">
                    <Search
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                    />
                    <input
                      type="text"
                      placeholder="Search features, glossary, questions…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="w-full pl-9 pr-9 py-2.5 rounded-xl bg-white/95 border border-white/30 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-400/50 shadow-lg"
                    />
                    {search && (
                      <button
                        type="button"
                        onClick={() => setSearch("")}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.header>

        {viewMode === "tour" ? (
          <div className="space-y-8">
            <LiveTourPanel />
            <details className="rounded-2xl border border-slate-200/80 bg-white/60 overflow-hidden">
              <summary className="cursor-pointer px-5 py-4 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                Wireframe preview (optional)
              </summary>
              <div className="border-t border-slate-200/80 p-4">
                <InteractiveProductTour />
              </div>
            </details>
          </div>
        ) : (
          <div>
            {/* Step wizard header — one section visible at a time instead of
             one long stacked scroll. Clicking a dot/label jumps straight
             to that section; Back/Next below move sequentially. */}
            <div className="mb-5">
              <div className="hidden lg:flex items-center gap-1.5">
                {HELP_SECTIONS.map((s, i) => {
                  const meta = DOC_STEP_META[s.id] || {};
                  const Icon = meta.icon || BookOpen;
                  const state =
                    i === docStep
                      ? "current"
                      : i < docStep
                        ? "done"
                        : "upcoming";
                  return (
                    <div
                      key={s.id}
                      className="flex items-center flex-1 last:flex-none"
                    >
                      <button
                        type="button"
                        onClick={() => goToDocStep(s.id)}
                        title={s.label}
                        className={`group flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold transition-all whitespace-nowrap ${
                          state === "current"
                            ? "bg-white shadow-md ring-1 ring-indigo-200"
                            : state === "done"
                              ? "text-slate-500 hover:bg-white/60"
                              : "text-slate-400 hover:bg-white/60"
                        }`}
                      >
                        <span
                          className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 text-[10px] font-bold"
                          style={{
                            background:
                              state === "upcoming"
                                ? "#e2e8f0"
                                : `${meta.color || "#6366f1"}18`,
                            color:
                              state === "upcoming"
                                ? "#94a3b8"
                                : meta.color || "#6366f1",
                          }}
                        >
                          {state === "done" ? (
                            <CheckCircle2 size={12} />
                          ) : state === "current" ? (
                            <Icon size={12} />
                          ) : (
                            i + 1
                          )}
                        </span>
                        <span
                          className={
                            state === "current" ? "text-slate-800" : ""
                          }
                        >
                          {s.label}
                        </span>
                      </button>
                      {i < HELP_SECTIONS.length - 1 && (
                        <div
                          className={`h-px flex-1 mx-1 ${
                            i < docStep ? "bg-indigo-300" : "bg-slate-200"
                          }`}
                        />
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Mobile: compact pills, horizontally scrollable */}
              <div className="flex lg:hidden gap-2 overflow-x-auto pb-1 scrollbar-thin">
                {HELP_SECTIONS.map((s, i) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => goToDocStep(s.id)}
                    className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      i === docStep
                        ? "bg-indigo-600 text-white border-indigo-600"
                        : "bg-white text-slate-600 border-slate-200"
                    }`}
                  >
                    <span className="text-[10px] font-bold">{i + 1}</span>
                    {s.label}
                  </button>
                ))}
              </div>

              <div className="flex items-center justify-between mt-3">
                <p className="text-xs text-slate-500">
                  Step {docStep + 1} of {HELP_SECTIONS.length}
                </p>
                <div className="h-1.5 flex-1 mx-4 rounded-full bg-slate-200 overflow-hidden max-w-xs">
                  <div
                    className="h-full bg-gradient-to-r from-indigo-500 to-sky-400 transition-all duration-300"
                    style={{
                      width: `${((docStep + 1) / HELP_SECTIONS.length) * 100}%`,
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Active section panel — fixed max-height with its own
             scrollbar, so the page around it (header, stepper, nav)
             never needs to move. */}
            <GlassCard>
              <div
                ref={contentPanelRef}
                className="p-5 sm:p-6 lg:p-8 overflow-y-auto"
                style={{ maxHeight: "min(70vh, 720px)" }}
              >
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeSectionId}
                    initial={{ opacity: 0, x: 12 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -12 }}
                    transition={{ duration: 0.2 }}
                  >
                    {activeSectionId === "overview" && (
                      <>
                        <SectionHeader
                          icon={Sparkles}
                          title="What is OneDPDP?"
                          subtitle="A simple guide to your privacy compliance workspace"
                        />
                        <p className="text-sm text-slate-600 leading-relaxed mb-5">
                          {SYSTEM_OVERVIEW.description}
                        </p>

                        <div className="grid sm:grid-cols-3 gap-3 mb-5">
                          {(SYSTEM_OVERVIEW.quickFacts || []).map((fact) => (
                            <div
                              key={fact.title}
                              className="rounded-xl p-4 bg-white/70 border border-slate-200/80"
                            >
                              <p className="text-sm font-semibold text-slate-800 mb-1">
                                {fact.title}
                              </p>
                              <p className="text-xs text-slate-600 leading-relaxed">
                                {fact.text}
                              </p>
                            </div>
                          ))}
                        </div>

                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-5">
                          {SYSTEM_OVERVIEW.pillars.map((p) => (
                            <div
                              key={p.title}
                              className="rounded-xl p-4 bg-gradient-to-br from-indigo-50/80 to-sky-50/80 border border-indigo-100/60"
                            >
                              <p className="text-sm font-semibold text-slate-800 mb-1">
                                {p.title}
                              </p>
                              <p className="text-xs text-slate-600 leading-relaxed">
                                {p.text}
                              </p>
                            </div>
                          ))}
                        </div>

                        <div className="rounded-xl p-4 bg-indigo-50/60 border border-indigo-100">
                          <p className="text-xs font-semibold text-indigo-700 uppercase tracking-wide mb-3">
                            Quick jumps
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {[
                              { id: "getting-started", label: "Getting Started" },
                              { id: "modules", label: "Features Guide" },
                              { id: "glossary", label: "Glossary" },
                              { id: "faq", label: "Common Questions" },
                            ].map((link) => (
                              <button
                                key={link.id}
                                type="button"
                                onClick={() => goToDocStep(link.id)}
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-white border border-indigo-200 text-indigo-700 hover:bg-indigo-50 transition-colors"
                              >
                                {link.label}
                                <ChevronRight size={12} />
                              </button>
                            ))}
                            <button
                              type="button"
                              onClick={() => setViewMode("tour")}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
                            >
                              <MousePointer2 size={12} />
                              Start Interactive Tour
                            </button>
                          </div>
                        </div>
                      </>
                    )}

                    {activeSectionId === "getting-started" && (
                      <>
                        <SectionHeader
                          icon={MousePointer2}
                          title="Getting Started"
                          subtitle="Follow these checklists — tick items off as you go"
                          color="#0ea5e9"
                        />
                        <p className="text-sm text-slate-600 leading-relaxed mb-5">
                          {GETTING_STARTED.intro}
                        </p>
                        <div className="space-y-4">
                          {GETTING_STARTED.checklists.map((checklist) => (
                            <GettingStartedChecklist
                              key={checklist.role}
                              checklist={checklist}
                              role={role}
                            />
                          ))}
                        </div>
                        <div className="mt-5 p-4 rounded-xl bg-sky-50/60 border border-sky-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                          <div>
                            <p className="text-sm font-semibold text-slate-800">
                              Ready to explore?
                            </p>
                            <p className="text-xs text-slate-600 mt-0.5">
                              Switch to Interactive Tour for a click-by-click
                              guide on real pages.
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setViewMode("tour")}
                            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold text-white bg-sky-600 hover:bg-sky-700 transition-colors shrink-0"
                          >
                            <MousePointer2 size={14} />
                            Open Interactive Tour
                          </button>
                        </div>
                      </>
                    )}

                    {activeSectionId === "roles" && (
                      <>
                        <SectionHeader
                          icon={Layers}
                          title="Who Does What"
                          subtitle="Three roles — each person sees a different menu on purpose"
                        />
                        <div className="grid md:grid-cols-3 gap-4">
                          {ROLES.map((r) => (
                            <div
                              key={r.id}
                              className={`rounded-xl p-4 border transition-all cursor-default ${
                                r.id === role
                                  ? "border-indigo-300 bg-indigo-50/50 ring-2 ring-indigo-200/50"
                                  : "border-slate-200/80 bg-white/40"
                              }`}
                            >
                              <div className="flex items-center gap-2 mb-2">
                                <span
                                  className="text-xs font-bold px-2 py-0.5 rounded-full text-white"
                                  style={{ background: r.color }}
                                >
                                  {r.badge}
                                </span>
                                {r.id === role && (
                                  <span className="text-[10px] text-indigo-600 font-semibold">
                                    You
                                  </span>
                                )}
                              </div>
                              <h3 className="font-semibold text-slate-800 text-sm mb-1">
                                {r.label}
                              </h3>
                              {r.plainSummary && (
                                <p className="text-[11px] text-indigo-600/90 mb-2 leading-relaxed italic">
                                  {r.plainSummary}
                                </p>
                              )}
                              <p className="text-xs text-slate-500 mb-3 leading-relaxed">
                                {r.description}
                              </p>
                              <ul className="space-y-1 mb-3">
                                {r.responsibilities.slice(0, 3).map((item) => (
                                  <li
                                    key={item}
                                    className="text-[11px] text-slate-600 flex gap-1.5"
                                  >
                                    <CheckCircle2
                                      size={10}
                                      className="text-emerald-500 shrink-0 mt-0.5"
                                    />
                                    {item}
                                  </li>
                                ))}
                              </ul>
                              <Link
                                to={r.home}
                                className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-700"
                              >
                                Go to {r.label} home
                                <ArrowRight size={12} />
                              </Link>
                            </div>
                          ))}
                        </div>
                      </>
                    )}

                    {activeSectionId === "journey" && (
                      <>
                        <SectionHeader
                          icon={Route}
                          title="Your Compliance Journey"
                          subtitle="From first setup to audit-ready reporting — who does what at each stage"
                          color="#0ea5e9"
                        />

                        <div className="mb-6 p-4 rounded-xl bg-slate-50/80 border border-slate-100">
                          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
                            Compliance Lifecycle (End User)
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {LIFECYCLE_STAGES.map((stage) => {
                              const Icon = stage.icon;
                              return (
                                <button
                                  key={stage.id}
                                  type="button"
                                  onClick={() =>
                                    role === "user" && navigate(stage.path)
                                  }
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all hover:scale-105"
                                  style={{
                                    borderColor: `${stage.color}40`,
                                    background: `${stage.color}10`,
                                    color: stage.color,
                                  }}
                                >
                                  <Icon size={12} />
                                  {stage.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        <div className="space-y-3">
                          {WORKFLOW_PHASES.map((phase) => (
                            <div
                              key={phase.step}
                              className="flex gap-4 p-4 rounded-xl bg-white/50 border border-slate-200/60 hover:border-indigo-200/60 transition-colors"
                            >
                              <div className="w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center text-sm font-bold shrink-0">
                                {phase.step}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-center gap-2 mb-1">
                                  <h3 className="font-semibold text-slate-800 text-sm">
                                    {phase.title}
                                  </h3>
                                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
                                    {phase.role}
                                  </span>
                                </div>
                                <p className="text-xs text-indigo-600 font-medium mb-1">
                                  {phase.summary}
                                </p>
                                <p className="text-xs text-slate-600 leading-relaxed">
                                  {phase.details}
                                </p>
                                {phase.path && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleModuleNavigate(phase.path)
                                    }
                                    className="mt-2 inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 font-medium"
                                  >
                                    <MapPin size={11} />
                                    Open module
                                    <ChevronRight size={11} />
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}

                    {activeSectionId === "modules" && (
                      <>
                        <SectionHeader
                          icon={BookOpen}
                          title="Features Guide"
                          subtitle="Every area of the app — click a card to expand plain-language instructions"
                        />

                        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
                          <div className="flex flex-wrap gap-2">
                            {[
                              { id: "all", label: "All Modules" },
                              { id: "admin", label: "Super Admin" },
                              { id: "tenant_admin", label: "Tenant Admin" },
                              { id: "user", label: "End User" },
                            ].map((tab) => (
                              <button
                                key={tab.id}
                                type="button"
                                onClick={() => setRoleFilter(tab.id)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                  roleFilter === tab.id
                                    ? "bg-indigo-600 text-white shadow-sm"
                                    : "bg-white/70 text-slate-600 border border-slate-200 hover:border-indigo-200"
                                }`}
                              >
                                {tab.label}
                              </button>
                            ))}
                          </div>

                          <div className="relative sm:ml-auto sm:w-64">
                            <Search
                              size={14}
                              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                            />
                            <input
                              type="text"
                              placeholder="Search modules…"
                              value={search}
                              onChange={(e) => setSearch(e.target.value)}
                              className="w-full pl-8 pr-8 py-1.5 rounded-lg bg-white border border-slate-200 text-xs text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-300/50"
                            />
                            {search && (
                              <button
                                type="button"
                                onClick={() => setSearch("")}
                                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                              >
                                <X size={12} />
                              </button>
                            )}
                          </div>
                        </div>

                        <p className="text-xs text-slate-500 mb-4">
                          {filteredModules.length} module
                          {filteredModules.length !== 1 ? "s" : ""} — click to
                          expand details
                        </p>

                        <div className="space-y-3">
                          {filteredModules.length === 0 ? (
                            <p className="text-sm text-slate-500 text-center py-8">
                              No modules match your search. Try different
                              keywords.
                            </p>
                          ) : (
                            filteredModules.map((mod, i) => (
                              <ModuleCard
                                key={mod.id}
                                module={mod}
                                defaultOpen={i === 0 && !search}
                                onNavigate={handleModuleNavigate}
                              />
                            ))
                          )}
                        </div>
                      </>
                    )}

                    {activeSectionId === "use-cases" && (
                      <>
                        <SectionHeader
                          icon={Lightbulb}
                          title="Real-Life Examples"
                          subtitle="How teams use OneDPDP in everyday situations"
                          color="#f59e0b"
                        />
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                          {USE_CASES.map((uc) => (
                            <div
                              key={uc.title}
                              className="rounded-xl p-4 bg-amber-50/50 border border-amber-100/80"
                            >
                              <h3 className="font-semibold text-slate-800 text-sm mb-1">
                                {uc.title}
                              </h3>
                              <p className="text-xs text-slate-500 mb-2 italic">
                                {uc.scenario}
                              </p>
                              <p className="text-xs text-slate-600 leading-relaxed">
                                <span className="font-medium text-amber-700">
                                  Flow:{" "}
                                </span>
                                {uc.flow}
                              </p>
                            </div>
                          ))}
                        </div>
                      </>
                    )}

                    {activeSectionId === "best-practices" && (
                      <>
                        <SectionHeader
                          icon={CheckCircle2}
                          title="Best Practices"
                          subtitle="Tips for getting the most out of OneDPDP"
                          color="#059669"
                        />
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                          {BEST_PRACTICES.map((bp, i) => (
                            <div
                              key={bp.title}
                              className="flex gap-3 p-3 rounded-xl bg-emerald-50/40 border border-emerald-100/60"
                            >
                              <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-bold shrink-0">
                                {i + 1}
                              </span>
                              <div>
                                <p className="text-sm font-semibold text-slate-800">
                                  {bp.title}
                                </p>
                                <p className="text-xs text-slate-600 mt-0.5 leading-relaxed">
                                  {bp.text}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}

                    {activeSectionId === "glossary" && (
                      <>
                        <SectionHeader
                          icon={BookOpen}
                          title="Simple Glossary"
                          subtitle="Privacy and platform terms explained without jargon"
                          color="#8b5cf6"
                        />
                        <div className="relative mb-4 sm:w-72">
                          <Search
                            size={14}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                          />
                          <input
                            type="text"
                            placeholder="Search terms…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full pl-8 pr-8 py-1.5 rounded-lg bg-white border border-slate-200 text-xs text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-300/50"
                          />
                          {search && (
                            <button
                              type="button"
                              onClick={() => setSearch("")}
                              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                            >
                              <X size={12} />
                            </button>
                          )}
                        </div>
                        <div className="space-y-2">
                          {filteredGlossary.length === 0 ? (
                            <p className="text-sm text-slate-500 text-center py-8">
                              No glossary terms match your search.
                            </p>
                          ) : (
                            filteredGlossary.map((item) => (
                              <GlossaryItem key={item.term} item={item} />
                            ))
                          )}
                        </div>
                      </>
                    )}

                    {activeSectionId === "faq" && (
                      <>
                        <SectionHeader
                          icon={HelpCircle}
                          title="Common Questions"
                          subtitle="Quick answers — click a question to expand"
                        />
                        <div className="relative mb-4 sm:w-72">
                          <Search
                            size={14}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                          />
                          <input
                            type="text"
                            placeholder="Search FAQ…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full pl-8 pr-8 py-1.5 rounded-lg bg-white border border-slate-200 text-xs text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-300/50"
                          />
                          {search && (
                            <button
                              type="button"
                              onClick={() => setSearch("")}
                              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                            >
                              <X size={12} />
                            </button>
                          )}
                        </div>
                        <div className="space-y-2">
                          {filteredFaqs.length === 0 ? (
                            <p className="text-sm text-slate-500 text-center py-8">
                              No FAQs match your search.
                            </p>
                          ) : (
                            filteredFaqs.map((item, i) => (
                              <FaqItem key={i} item={item} index={i} />
                            ))
                          )}
                        </div>
                      </>
                    )}

                    {activeSectionId === "troubleshooting" && (
                      <>
                        <SectionHeader
                          icon={Wrench}
                          title="Fix Common Problems"
                          subtitle="Something not working? Start here"
                          color="#dc2626"
                        />
                        <div className="relative mb-4 sm:w-72">
                          <Search
                            size={14}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                          />
                          <input
                            type="text"
                            placeholder="Search issues…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full pl-8 pr-8 py-1.5 rounded-lg bg-white border border-slate-200 text-xs text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-300/50"
                          />
                          {search && (
                            <button
                              type="button"
                              onClick={() => setSearch("")}
                              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                            >
                              <X size={12} />
                            </button>
                          )}
                        </div>
                        <div className="space-y-3">
                          {filteredTrouble.length === 0 ? (
                            <p className="text-sm text-slate-500 text-center py-8">
                              No issues match your search.
                            </p>
                          ) : (
                            filteredTrouble.map((t) => (
                              <div
                                key={t.issue}
                                className="rounded-xl border border-red-100/80 bg-white/50 overflow-hidden"
                              >
                                <div className="flex items-start gap-3 p-4">
                                  <AlertCircle
                                    size={18}
                                    className="text-red-500 shrink-0 mt-0.5"
                                  />
                                  <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-slate-800 text-sm">
                                      {t.issue}
                                    </p>
                                    <div className="mt-2 grid sm:grid-cols-2 gap-3">
                                      <div>
                                        <p className="text-[10px] font-semibold text-slate-400 uppercase mb-1">
                                          Possible Causes
                                        </p>
                                        <ul className="space-y-0.5">
                                          {t.causes.map((c) => (
                                            <li
                                              key={c}
                                              className="text-xs text-slate-600"
                                            >
                                              • {c}
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                      <div>
                                        <p className="text-[10px] font-semibold text-emerald-600 uppercase mb-1">
                                          How to Fix
                                        </p>
                                        <p className="text-xs text-slate-600 leading-relaxed">
                                          {t.fix}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </>
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Wizard footer nav */}
              <div className="flex items-center justify-between gap-3 border-t border-slate-100 px-5 sm:px-6 py-3.5 bg-slate-50/60">
                <button
                  type="button"
                  onClick={prevDocStep}
                  disabled={docStep === 0}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold text-slate-600 border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft size={14} />
                  Back
                </button>
                <span className="text-xs text-slate-400 hidden sm:inline">
                  {HELP_SECTIONS[docStep]?.label}
                </span>
                {docStep < HELP_SECTIONS.length - 1 ? (
                  <button
                    type="button"
                    onClick={nextDocStep}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 transition-colors"
                  >
                    Next
                    <ChevronRight size={14} />
                  </button>
                ) : (
                  <Link
                    to={roleInfo.home}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 transition-colors"
                  >
                    Go to Dashboard
                    <ArrowRight size={14} />
                  </Link>
                )}
              </div>
            </GlassCard>
          </div>
        )}
      </div>
    </div>
  );
}
